<template>
    <section class="m-mKasaWidget px-4 pt-4" style="padding-bottom: 2.5rem">
        <div
            class="d-flex flex-wrap align-items-center justify-content-between mb-4"
            style="grid-gap: 1rem"
        >
            <div class="custom-chart-header">
                <h2 class="h3 mb-0">
                    Realizacja warunków porozumienia M/kasa
                </h2>
                <span class="subtitle">
                    <span class="star text-dark">*</span>
                    ostatnia aktualizacja:
                    {{
                        $moment()
                            .subtract(1, 'days')
                            .format('DD.MM.YYYY')
                    }}r.
                </span>
            </div>
            <the-dropdown
                v-if="$store.getters.getLocationIdsForSelect"
                :options="$store.getters.getLocationIdsForSelect"
                @onChange="onDropdownChange($event)"
                :selectedOption="
                    widgetLocationId ? widgetLocationId.toString() : null
                "
                text="Wybierz sklep..."
            >
            </the-dropdown>
        </div>
        <page-preloader
            v-if="status === 'loading'"
            minHeight="254px"
        ></page-preloader>
        <div class="row" v-else-if="report">
            <div
                class="col col-xl-3 col-sm-6 col-12 d-flex flex-sm-row flex-column text-center text-sm-left align-items-center gap-1"
                v-for="(chart, idx) in report.charts"
                :key="idx"
            >
                <vue-apex-charts
                    class="m-mKasaWidget__chart"
                    :class="charts[idx].series[0] === 100 ? '--success' : ''"
                    :width="102"
                    :height="charts[idx].chart.height"
                    :series="charts[idx].series"
                    :options="charts[idx]"
                ></vue-apex-charts>
                <div>
                    <h6 class="mb-1">
                        {{ chart.heading }}
                        <a-icon
                            v-if="chart.tooltip"
                            class="ml-1"
                            icon="Info"
                            v-tooltip="chart.tooltip"
                            style="width: 18px; height: 18px; transform: translateY(3px);"
                        />
                    </h6>
                    <p v-if="chart.subheading" class="text-sm">
                        {{ chart.subheading }}
                    </p>
                </div>
            </div>
        </div>
        <div
            class="row justify-content-center mt-4 flex-column align-items-center"
            v-if="report && status !== 'loading'"
        >
            <the-btn class="btn-outline-custom" @click.native="goToReport"
                ><a-icon class="mr-2" icon="Report" />Przejdź do pełnego
                raportu</the-btn
            >
        </div>
        <div
            class="row justify-content-center mt-0"
            v-if="!report && status !== 'loading'"
        >
            Brak raportów dla wybranego sklepu.
        </div>
    </section>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
export default {
    components: {
        VueApexCharts
    },
    computed: {
        report() {
            const mKasa = this.$store.getters.getMKasa
            if (!mKasa || !mKasa[0].data.length || !mKasa[1].data.length) return

            const saleItems = mKasa[0].data
            const promoItems = mKasa[1].data
            const weekestMKasaReport = promoItems.reduce(function(prev, curr) {
                return prev.promo_with_usage_count_avg <
                    curr.promo_with_usage_count_avg
                    ? prev
                    : curr
            })
            const saleItem = saleItems.find(
                el =>
                    el.year === weekestMKasaReport.year &&
                    el.month === weekestMKasaReport.month
            )
            weekestMKasaReport.charts = []

            const usagePercent = progressPercent(
                weekestMKasaReport.promo_with_usage_count_avg,
                weekestMKasaReport.promo_limit_avg
            )
            const usageLevel = progressLevel(usagePercent)
            const usageHeading = 'Użyte promocje'
            const usageTooltip = 'Liczba promocji użytych przynajmniej 1 raz'
            const usageSubheading = `${+weekestMKasaReport.promo_with_usage_count_avg.toFixed(
                1
            )} / ${+weekestMKasaReport.promo_limit_avg.toFixed()}`
            weekestMKasaReport.charts.push({
                percent: usagePercent,
                tooltip: usageTooltip,
                level: usageLevel,
                heading: usageHeading,
                subheading: usageSubheading
            })

            const totalPercent = progressPercent(
                saleItem.total_rev.toFixed(),
                100000
            )
            const totalLevel = progressLevel(totalPercent)
            const totalHeading = 'Obrót ogółem'
            const totalTooltip =
                'Wartość brutto sprzedanych towarów<br/><i>Realizacja / Cel</i>'
            const totalSubheading = `${this.$options.filters.number(
                saleItem.total_rev.toFixed()
            )} / 100 000`
            weekestMKasaReport.charts.push({
                percent: totalPercent,
                tooltip: totalTooltip,
                level: totalLevel,
                heading: totalHeading,
                subheading: totalSubheading
            })

            const beerPercent = progressPercent(saleItem.beer_rev, 5000)
            const beerLevel = progressLevel(beerPercent)
            const beerHeading = 'Obrót piwem'
            const beerTooltip =
                'Wartość brutto sprzedanych towarów z kategorii piwo<br/><i>Realizacja / Cel</i>'
            const beerSubheading = `${
                saleItem.beer_rev
                    ? this.$options.filters.number(saleItem.beer_rev.toFixed())
                    : 0
            } / 5 000`
            weekestMKasaReport.charts.push({
                percent: beerPercent,
                tooltip: beerTooltip,
                level: beerLevel,
                heading: beerHeading,
                subheading: beerSubheading
            })

            const eanPercent = progressPercent(
                saleItem.full_ean_rev,
                saleItem.total_rev
            )
            const eanLevel = progressLevel(beerPercent)
            const eanHeading = 'Sprzedaż po kodach EAN'
            const eanTooltip =
                'Stosunek sprzedaży z wykorzystaniem kodów EAN do ogólnej sprzedaży<br/><i>Realizacja / Cel</i>'
            const eanSubheading = `${this.$options.filters.number(
                saleItem.full_ean_rev.toFixed()
            )} / ${this.$options.filters.number(saleItem.total_rev.toFixed())}`
            weekestMKasaReport.charts.push({
                percent: eanPercent,
                tooltip: eanTooltip,
                level: eanLevel,
                heading: eanHeading,
                subheading: eanSubheading
            })

            function progressPercent(avg, limit) {
                if (avg === 0 || limit === 0) return 0
                let percent = (avg * 100) / limit
                if (percent > 100) percent = 100
                return +percent.toFixed()
            }
            function progressLevel(percent) {
                let level = 'very-low'
                if (percent >= 20 && percent <= 39) {
                    level = 'low'
                }
                if (percent >= 40 && percent <= 59) {
                    level = 'medium'
                }
                if (percent >= 60 && percent <= 79) {
                    level = 'high'
                }
                if (percent >= 80 && percent <= 99) {
                    level = 'very-high'
                }
                if (percent === 100) {
                    level = 'success'
                }
                return level
            }
            return { ...weekestMKasaReport, ...saleItem }
        },
        charts() {
            const report = this.report
            if (!report) return
            return report.charts.map(chart => {
                return {
                    chart: {
                        height: 111,
                        type: 'radialBar',
                        sparkline: {
                            enabled: true
                        }
                    },
                    grid: {
                        show: false,
                        padding: {
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0
                        }
                    },

                    series: [chart.percent],

                    plotOptions: {
                        radialBar: {
                            hollow: {
                                size: '70px'
                            },

                            dataLabels: {
                                showOn: 'always',
                                name: {
                                    offsetY: 8,
                                    show: true,
                                    color: `var(--color-progress--${chart.level})`,
                                    fontSize: '1.5rem',
                                    fontFamily: 'var(--font-family-sans-serif)'
                                },
                                value: {
                                    color: `var(--color-progress--${chart.level})`,
                                    fontSize: '2rem',
                                    fontFamily: 'var(--font-family-sans-serif)',
                                    fontWeight: 700,
                                    show: false
                                }
                            }
                        }
                    },
                    labels: [chart.percent + '%'],
                    fill: {
                        colors: [`var(--color-progress--${chart.level})`]
                    },
                    stroke: {
                        lineCap: 'round'
                    }
                }
            })
        },
        widgetLocationId() {
            return this.$store.getters.getMKasaWidgetLocationId
        },
        status() {
            return this.$store.getters.getMKasaStatus
        }
    },
    created() {
        if (
            this.$store.getters.getLocationIdsForSelect[0] &&
            !this.widgetLocationId
        ) {
            this.$store.dispatch(
                'setMKasaWidgetLocationId',
                this.$store.getters.getLocationIdsForSelect[0].value
            )
        }
        this.fetch()
    },
    methods: {
        fetch() {
            this.$store.dispatch('mKasaFetch', {
                data_for_widget: 1,
                location_id: this.widgetLocationId
            })
        },
        onDropdownChange(locationId) {
            this.$store.dispatch('setMKasaWidgetLocationId', locationId)
            this.fetch()
        },
        goToReport() {
            this.$store.dispatch('setFilter', {
                filter: 'shopIds',
                value: this.widgetLocationId,
                moduleName: 'mKasa'
            })
            this.$router.push({ name: 'mKasa' })
        }
    }
}
</script>
